/**
 * The agent panel's open chat tabs: the threads opened this session, in the
 * order they were opened, the way Cursor's chat header keeps a tab strip
 * separate from the full history. The active thread is always a tab; closing
 * a tab never deletes the thread. Persisted per case so a reload keeps them.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CaseAgentThread } from "../api/caseAgentApi";

export const openTabsKey = (individualId: string): string =>
  `caseIde.chatTabs.${individualId}`;

const readIds = (individualId: string): string[] => {
  try {
    const raw = window.localStorage.getItem(openTabsKey(individualId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x): x is string => typeof x === "string")
      : [];
  } catch {
    return [];
  }
};

const writeIds = (individualId: string, ids: string[]) => {
  try {
    if (ids.length)
      window.localStorage.setItem(
        openTabsKey(individualId),
        JSON.stringify(ids)
      );
    else window.localStorage.removeItem(openTabsKey(individualId));
  } catch {
    // Not remembered; harmless.
  }
};

type Chat = {
  threads: CaseAgentThread[];
  threadsLoading: boolean;
  threadId: string | null;
  setThreadId: (id: string | null) => void;
};

export type OpenThreadTabs = {
  /** Open tabs in strip order (only threads that still exist). */
  openThreads: CaseAgentThread[];
  /** Close a tab. Closing the active one activates its nearest neighbour. */
  close: (id: string) => void;
  closeOthers: (id: string) => void;
};

export const useOpenThreadTabs = (
  individualId: string,
  chat: Chat
): OpenThreadTabs => {
  const { threads, threadsLoading, threadId, setThreadId } = chat;
  const [ids, setIds] = useState<string[]>(() => readIds(individualId));
  // Persist through a ref so the close handlers can update ids and storage
  // together without a stale closure.
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const update = useCallback(
    (next: string[]) => {
      idsRef.current = next;
      setIds(next);
      writeIds(individualId, next);
    },
    [individualId]
  );

  // Another case: start from what that case remembers.
  const lastIndividual = useRef(individualId);
  useEffect(() => {
    if (lastIndividual.current === individualId) return;
    lastIndividual.current = individualId;
    update(readIds(individualId));
  }, [individualId, update]);

  // The active thread is always a tab (appended when first selected).
  useEffect(() => {
    if (!threadId || idsRef.current.includes(threadId)) return;
    update([...idsRef.current, threadId]);
  }, [threadId, update]);

  // Threads deleted elsewhere (a teammate, another tab) drop out of the strip.
  useEffect(() => {
    if (threadsLoading) return;
    const known = new Set(threads.map((t) => t.id));
    const kept = idsRef.current.filter((id) => known.has(id));
    if (kept.length !== idsRef.current.length) update(kept);
  }, [threads, threadsLoading, update]);

  const openThreads = useMemo(() => {
    const byId = new Map(threads.map((t) => [t.id, t]));
    return ids.flatMap((id) => {
      const t = byId.get(id);
      return t ? [t] : [];
    });
  }, [ids, threads]);

  const close = useCallback(
    (id: string) => {
      const cur = idsRef.current;
      const at = cur.indexOf(id);
      if (at < 0) return;
      const rest = cur.filter((x) => x !== id);
      update(rest);
      if (threadId === id) {
        setThreadId(rest[Math.min(at, rest.length - 1)] ?? null);
      }
    },
    [setThreadId, threadId, update]
  );

  const closeOthers = useCallback(
    (id: string) => {
      if (!idsRef.current.includes(id)) return;
      update([id]);
      if (threadId !== id) setThreadId(id);
    },
    [setThreadId, threadId, update]
  );

  return { openThreads, close, closeOthers };
};
